import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatch, useResolvedPath } from "react-router-dom";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useTheme from "@mui/material/styles/useTheme";

import { useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import logo from "assets/icons/suno-logo-white.svg";
import SupaDemoButton from "components/supa-demo-button";
import { INBOX_UNREAD_COUNTS } from "constants/feature-flags";
import { SUPADEMO_GUIDED_TOUR_ID } from "constants/supa-demo";
import { useSideMenuChoice } from "features/app-layout/side-menu-choice/use-side-menu-choice.hook";
import { useSwitchSideMenu } from "features/app-layout/side-menu-choice/use-switch-side-menu.hook";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGlobalChat } from "features/chat/use-global-chat.hook";
import { getIncludeMessagesFromOtherClinicsState } from "features/inbox/inbox-slice";
import useTextRoutingCounts from "features/inbox/text-routing/use-text-routing-counts.hook";
import useDeferredReady from "hooks/use-deferred-ready.hook";
import useIsMobile from "hooks/use-is-mobile.hook";
import useTextRoutingEnabled from "hooks/use-text-routing-enabled.hook";
import useTouchpointsCount from "hooks/use-touchpoints-count";
import { useGetUnreadFaxCountQuery } from "services/clinics";
import { useGetSuggestedCollectionsQuery } from "services/collections";
import { useGetUnreadMessagesCountQuery } from "services/messages";

import useVisibleMenuItems from "../use-visible-menu-items.hook";
import LegacySideMenuItem from "./legacy-side-menu-item.component";

/**
 * ENG-2535: the classic rail, exactly as it was before the redesign, rendered while the
 * `sidebar-redesign` flag is off or the person switched back. `MENU` is now in the new
 * display order, so this keeps the old order itself. Delete this folder with the flag.
 */
const LEGACY_ORDER = [
  "/home",
  "/onboarding",
  "/practice-sessions",
  "/dashboard",
  "/inbox",
  "/chat",
  "/scheduling",
  "/scheduling-v3",
  "/patients",
  "/billing",
  "/collections",
  "/eras",
  "/vobs",
  "/reports",
  "/marketing",
  "/reviews",
  "/medical-scribes",
  "/sales-coach",
  "/action-items",
  "/calls",
  "/leads",
  "/faxes-portal",
  "/inventory",
  "/marketplace",
  "/sops",
  "/settings",
  "/quickbooks"
];
const legacyRank = (to: string) => {
  const index = LEGACY_ORDER.indexOf(to);
  return index === -1 ? LEGACY_ORDER.length : index;
};
/** Labels the old rail wrapped onto two lines (its `allowWrap`), and the one divider it drew. */
const WRAPPED_LABELS = new Set(["/practice-sessions", "/scheduling-v3"]);
const DIVIDER_AFTER = "/marketplace";

const layoutContainerSx = {
  justifyContent: "flex-start",
  alignItems: "center",
  pt: 1,
  px: 2
};

export default function LegacySideMenu() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const includeMessagesFromOtherClinics = useSelector(
    getIncludeMessagesFromOtherClinicsState
  );
  const { assignedTouchpointsCount } = useTouchpointsCount();

  let resolved = useResolvedPath("/");
  const isDefaultPath = Boolean(
    useMatch({ path: resolved.pathname, end: true })
  );

  const [showMenu, setShowMenu] = useState(!isMobile);

  useEffect(() => {
    setShowMenu(!isMobile);
  }, [isMobile]);

  const currentClinic = useAppSelector(selectCurrentClinic);
  const flags = useFlags();
  const { unreadCount } = useGlobalChat();
  const deferredReady = useDeferredReady();
  // Patient text routing: the Inbox badge counts what is waiting on ME, not
  // unread messages. Routing off => the unread query below runs exactly as before.
  // routingOn includes Inbox V2 (the routing views exist only there), so V1
  // keeps the unread badge.
  const { routingOn } = useTextRoutingEnabled();
  const { counts: routingCounts } = useTextRoutingCounts({
    skip: !deferredReady || !currentClinic?.id || !routingOn,
    pollingInterval: 60000
  });
  const { data: unreadMessagesCount } = useGetUnreadMessagesCountQuery(
    {
      clinicId: includeMessagesFromOtherClinics ? undefined : currentClinic?.id,
      clinics:
        includeMessagesFromOtherClinics && hasRegions
          ? currentRegionClinicsIds
          : undefined,
      byPatientClinic: Boolean(flags[INBOX_UNREAD_COUNTS])
    },
    {
      pollingInterval: 3600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );

  const { data: unreadFaxCount } = useGetUnreadFaxCountQuery(
    { clinicId: currentClinic?.id! },
    {
      pollingInterval: 600000,
      skip: !deferredReady || !currentClinic?.id
    }
  );
  const { data: suggestedCollect } = useGetSuggestedCollectionsQuery(
    { limit: 0 },
    {
      pollingInterval: 600000,
      // Enrollment, not just the permission: a suggestion is an invitation to START a
      // collection, which is exactly what deactivation stops (ENG-1968). An unenrolled
      // practice has no settings row either, and the endpoint 500s without one.
      skip:
        !deferredReady ||
        !currentUser?.has_access_to_collections ||
        !currentPractice?.enrolled_in_collections
    }
  );

  // Visibility (roles, permissions, flags, practice/user columns) now lives in
  // its own hook so the quick launcher can offer exactly the destinations this
  // user can reach. This component keeps only the badges and spacing.
  const visibleItems = useVisibleMenuItems()
    .slice()
    .sort((a, b) => legacyRank(a.to) - legacyRank(b.to))
    .map((item) => ({
      ...item,
      allowWrap: WRAPPED_LABELS.has(item.to),
      divider: item.to === DIVIDER_AFTER
    }));
  const { offered } = useSideMenuChoice();
  const { switchToNew } = useSwitchSideMenu();

  const Menu = useMemo(() => {
    return visibleItems.map((menuItem, index) => {
      if (menuItem.primary === "Inbox") {
        return {
          ...menuItem,
          badgeContent: routingOn
            ? routingCounts?.mine || 0
            : unreadMessagesCount || 0,
          // The badge changes meaning with routing on (waiting on me, not unread): say so.
          ...(routingOn
            ? {
                tooltip: `${menuItem.tooltip}. The badge counts conversations waiting on you`
              }
            : {})
        };
      }

      if (menuItem.primary === "Chat") {
        return {
          ...menuItem,
          badgeContent: unreadCount || 0
        };
      }

      if (menuItem.primary === "Marketing") {
        return { ...menuItem, badgeContent: assignedTouchpointsCount };
      }

      if (menuItem.primary === "Collect") {
        return {
          ...menuItem,
          badgeContent: suggestedCollect?.count || 0
        };
      }

      if (menuItem.primary === "Faxes") {
        return {
          ...menuItem,
          badgeContent: unreadFaxCount?.count || 0
        };
      }

      if (index === visibleItems.length - 1) {
        return { ...menuItem, sx: { mb: 15 } };
      } else {
        return menuItem;
      }
    });
  }, [
    visibleItems,
    unreadMessagesCount,
    routingOn,
    routingCounts?.mine,
    unreadCount,
    assignedTouchpointsCount,
    unreadFaxCount?.count,
    suggestedCollect?.count
  ]);

  const layoutSx = useMemo(() => {
    return {
      width: "100px",
      height: "100vh",
      overflow: "auto",
      background: theme.palette.sideMenu.main,
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      },
      display: showMenu ? "block" : "none",
      position: isMobile ? "absolute" : "relative",
      zIndex: 3,
      boxShadow: isMobile ? "20px 0 15px -12px rgba(0, 0, 0, 0.7)" : "none"
    };
  }, [theme.palette.sideMenu.main, isMobile, showMenu]);

  const menuItems = useMemo(() => {
    return Menu.map(({ to, tooltip, ...props }) => (
      <LegacySideMenuItem
        key={to}
        isDefaultPath={isDefaultPath}
        to={to}
        tooltip={tooltip}
        {...props}
      />
    ));
  }, [Menu, isDefaultPath]);

  if (isMobile && !showMenu) {
    return (
      <Box
        sx={{
          position: "absolute",
          left: 2,
          top: 2
        }}
      >
        <Button
          sx={{
            background: theme.palette.sideMenu.main,
            color: "white",
            minWidth: "38px",
            minHeight: "38px",
            boxShadow: "2px 2px 7px 2px rgba(0, 0, 0, 0.4)"
          }}
          style={{ padding: 0 }}
          size="small"
          onClick={() => setShowMenu(true)}
        >
          <MenuIcon sx={{ width: "27px", height: "27px" }} />
        </Button>
      </Box>
    );
  }
  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isMobile) {
          setShowMenu(false);
        }
      }}
    >
      <Box>
        {isMobile && (
          <IconButton
            size="small"
            sx={{
              color: "white",
              position: "absolute",
              left: "94px",
              top: "3px",
              background: theme.palette.sideMenu.main,
              border: `solid 1px white`,
              zIndex: 100
            }}
            onClick={() => setShowMenu(false)}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Stack spacing={1} sx={layoutSx} data-testid="layout-side-menu">
          <Stack sx={layoutContainerSx}>
            <Box
              component="img"
              src={logo}
              alt="logo"
              maxWidth={theme.typography.pxToRem(80)}
            />
            <SupaDemoButton
              isSideMenu
              supaDemoId={SUPADEMO_GUIDED_TOUR_ID}
              tooltip="Guided Tour"
              label="guided-tour"
              sx={{ height: "40px", mt: 1, mb: -1 }}
            />
            {offered && (
              <Tooltip
                title="Try the new menu: grouped, and it can shrink to a rail. You can switch back any time."
                placement="right"
                describeChild
              >
                <Button
                  size="small"
                  variant="borderless"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => void switchToNew("classic_rail")}
                  sx={{
                    mt: 2,
                    color: neutral[50],
                    border: `1px solid ${neutral[50]}`,
                    fontSize: theme.typography.pxToRem(12),
                    lineHeight: 1.2,
                    textAlign: "center",
                    width: "100%"
                  }}
                >
                  New menu
                </Button>
              </Tooltip>
            )}
          </Stack>
          <List>{menuItems}</List>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
